import { useEffect, useId, useState } from 'react';

/**
 * Gate de confirmación para acciones DESTRUCTIVAS IRREVERSIBLES (hard-delete).
 *
 * Combina dos fricciones deliberadas para que un borrado definitivo no se
 * dispare de un clic reflejo:
 *  1. Contador hacia atrás: el botón de confirmar arranca deshabilitado N
 *     segundos (visible en el label) para forzar una pausa.
 *  2. Doble confirmación type-to-confirm: hay que escribir un texto exacto
 *     (el nombre/SKU del recurso, o "ELIMINAR" en borrados masivos) para
 *     habilitar el confirm.
 *
 * INVARIANTE: `gateOk` solo debe alimentar el `disabled` del botón de CONFIRMAR.
 * Nunca el cancel ni el Escape — esos siguen funcionando siempre (si no, un
 * modal trabado por el contador no se podría cerrar).
 *
 * Solo aplica a borrados irreversibles. Archivar/desactivar (reversible) sigue
 * con su confirmación simple.
 */
export function useConfirmGate({
  countdownSeconds = 0,
  requireText,
}: {
  countdownSeconds?: number;
  requireText?: string | null;
}): {
  remaining: number;
  typed: string;
  setTyped: (v: string) => void;
  gateOk: boolean;
} {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [typed, setTyped] = useState('');

  // Deps = SOLO el primitivo estable `countdownSeconds`. Si entrara algo
  // inestable (ej: `requireText` derivado, o el `body` que recomputa cuando un
  // conteo loading→loaded llega), el contador se reiniciaría a mitad de tick.
  useEffect(() => {
    if (!countdownSeconds || countdownSeconds <= 0) {
      setRemaining(0);
      return;
    }
    setRemaining(countdownSeconds);
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [countdownSeconds]);

  // trim + case-insensitive: una categoría "Niños", un nombre con un espacio
  // sobrante o un SKU en otra capitalización NO deben trabar al dueño de su
  // propio borrado. "ELIMINAR" matchea igual sin importar el case.
  const normalize = (s: string) => s.trim().toLocaleLowerCase();
  const textOk = !requireText || normalize(typed) === normalize(requireText);
  const gateOk = remaining === 0 && textOk;

  return { remaining, typed, setTyped, gateOk };
}

/**
 * Campo de texto del gate type-to-confirm. Presentacional: el estado vive en
 * `useConfirmGate` del modal que lo consume. Estilo consistente en las 8
 * superficies de hard-delete del módulo.
 */
export function TypeToConfirmField({
  requireText,
  typed,
  onChange,
  disabled = false,
}: {
  requireText: string;
  typed: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div style={{ marginTop: 12 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 13,
          color: 'var(--text-2)',
          marginBottom: 6,
        }}
      >
        Escribe{' '}
        <code
          style={{
            background: 'var(--bg-2)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--r-1)',
            padding: '1px 6px',
            fontWeight: 600,
            color: 'var(--text-1)',
          }}
        >
          {requireText}
        </code>{' '}
        para confirmar
      </label>
      <input
        id={id}
        type="text"
        className="input mono"
        style={{ width: '100%' }}
        value={typed}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-label={`Escribe ${requireText} para confirmar`}
      />
    </div>
  );
}
